import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import type { ClientOngoingLoad } from '@numeo/types-core'
import { PaymentTerms } from '@numeo/types-core'
import { invoicingStatusOf, type CreateInvoiceParams, type InvoicingStatus, type TmsInvoiceRow } from '@numeo/types-main-web-app'
import { Button, Skeleton, Spinner } from '@/components/ds2'
import { SnackbarType, showSnackbar } from '@/utils/snackbar'
import { useInvoicingChat } from '../../../invoicing/chatBridge'
import { useInvoicingRowsStore } from '../../../invoicing/rowsStore'
import { useFactoringBatchableStore } from '../../../invoicing/factoringBatchableStore'
import { POD_CHECK_POLL_MS, POD_CHECK_WAIT_MS } from '../../../hooks/usePodCheckWatch'
import { formatDate } from '../../results/format'
import { parseRateAmount } from '../../results/rowActions'
import { PanelDrawer } from '../PanelDrawer'
import { InvoicingStatusBadge } from '../InvoicingStatusBadge'
import { badgeLabelOf } from '../invoicingRows'
import { DocumentView } from './DocumentView'
import { RateConView, placeOf } from './RateConView'
import { DocumentsSection } from './DocumentsSection'
import { DraftPreview } from './DraftPreview'
import { InvoiceDetailsSection, type InvoiceDetailsValues } from './InvoiceDetailsSection'
import { SkipSubmit } from './SkipSubmit'
import { approveAndCreate, confirmDocumentMatch, skipAndCreate, type StepResult } from './reviewCalls'
import {
    DEFAULT_PAYMENT_TERMS,
    canApprove,
    chargeErrors,
    confirmReasonError,
    createParamsOf,
    invoiceTotalOf,
    rateConMissingOf,
    ratePendingOf,
    skipReasonError,
    todayIsoDate,
} from './reviewModel'
import { refreshInvoicingRow } from './refreshRow'
import { refreshLoadRow } from '../../results/freshRows'
import { useInvoiceReview, type ReviewMode } from './reviewStore'
import { useOngoingLoad } from './useOngoingLoad'
import { useDrawerUploads } from './useDrawerUploads'

const DRAFT_CREATED = 'Invoice draft created in Accounting → Invoices (AR).'

/** The route box's height (its padding, the labels, the place and the date): the skeleton holds it while the load is read. */
const ROUTE_BOX_HEIGHT = 96

/**
 * The drawer's pickup → delivery box: the first and the last stop of the load. While the load is read, a skeleton of the box's own
 * height holds its place, so nothing below moves when it arrives. Nothing is shown when the board has no route.
 */
const RouteBox = ({ load, loading }: { load: ClientOngoingLoad | undefined; loading: boolean }) => {
    const route = load?.route ?? []
    if (route.length === 0) {
        if (!loading) return null
        return (
            <div className="my-4" role="status" aria-label="Loading the route">
                <Skeleton height={ROUTE_BOX_HEIGHT} radius={10} />
            </div>
        )
    }
    const pickup = route[0]
    const delivery = route[route.length - 1]
    return (
        <div className="my-4 flex min-h-24 flex-wrap items-start justify-between gap-4 rounded-[10px] bg-surface-secondary p-4">
            <div className="flex min-w-0 flex-col">
                <small className="text-xs text-foreground-muted">PICKUP</small>
                <strong className="my-1.5 text-sm font-semibold text-foreground">{placeOf(pickup) ?? '—'}</strong>
            </div>
            <span className="self-center text-foreground-muted" aria-hidden>
                →
            </span>
            <div className="flex min-w-0 flex-col">
                <small className="text-xs text-foreground-muted">DELIVERY</small>
                <strong className="my-1.5 text-sm font-semibold text-foreground">{placeOf(delivery) ?? '—'}</strong>
                {delivery.date && <small className="text-xs text-foreground-muted">{formatDate(delivery.date)}</small>}
            </div>
        </div>
    )
}

/** The payment terms a draft takes: the row's own, else the default. */
const defaultTermsOf = (row: TmsInvoiceRow): string => row.payment_terms ?? DEFAULT_PAYMENT_TERMS

interface ReviewShellProps {
    onClose: () => void
    title: string
    subtitle: string
    footer: ReactNode
    children: ReactNode
}

/** The one right-hand panel every view of the review opens in (v17 drawer): a heading with its load, the view's body and its footer. */
const ReviewShell = ({ onClose, title, subtitle, footer, children }: ReviewShellProps) => (
    <PanelDrawer onClose={onClose} title={title} subtitle={subtitle} footer={footer}>
        {children}
    </PanelDrawer>
)

interface ReviewBodyProps {
    loadNumber: string
    /** The load's id when the opener knew it; the invoicing row's own id wins once it is read. */
    loadId: string | null
    /** The view the drawer opens on: `skip` opens the skip form, `document` the proof of delivery. */
    mode: ReviewMode
    row: TmsInvoiceRow | undefined
    onClose: () => void
}

/** The review of one load, keyed by its number and view: the drawer's state starts fresh for each one it opens on. */
const ReviewBody = ({ loadNumber, loadId: openedLoadId, mode, row, onClose }: ReviewBodyProps) => {
    const chat = useInvoicingChat()
    // The bridge may be a new object per render: the effects read the latest one through a ref, so they run once per load.
    const chatRef = useRef(chat)
    chatRef.current = chat
    const [view, setView] = useState<'review' | 'document' | 'rate'>(mode === 'document' ? 'document' : 'review')
    // The first read of the load's invoicing row finished: with no row then, the load is not on the invoicing list.
    const [rowRead, setRowRead] = useState(false)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [confirmReason, setConfirmReason] = useState('')
    // Skip & submit's first click shows the warning and the reason field; the footer button then reads "Confirm skip & submit".
    const [skipping, setSkipping] = useState(mode === 'skip')
    const [skipReason, setSkipReason] = useState('')
    // The date the form opens on; a date left on it is not sent, so the server judges "today" on its own clock.
    const [openedOn] = useState(todayIsoDate)
    const [details, setDetails] = useState<InvoiceDetailsValues>({ invoiceNumber: '', invoiceDate: openedOn, terms: '', charges: [], rateText: '' })
    // The status the row had when a POD was filed: the row is read again until it moves (the check finished).
    const [awaiting, setAwaiting] = useState<InvoicingStatus | null>(null)
    const [version, setVersion] = useState(0)
    const { load, loading: loadLoading } = useOngoingLoad(row?.load_id ?? openedLoadId ?? undefined, version)
    const boardLoadId = row?.load_id ?? openedLoadId ?? undefined

    // A file the drawer filed (or a revision accepted here): the row and the load are read again, and an older card of the load follows.
    const reread = useCallback(() => {
        void chatRef.current
            .ensureTab()
            .then((tabId) => refreshInvoicingRow(tabId, loadNumber))
            .then(() => setVersion((value) => value + 1))
            .catch(() => undefined)
        if (boardLoadId) void refreshLoadRow(boardLoadId, loadNumber)
    }, [loadNumber, boardLoadId])
    const podFiled = useCallback(() => setAwaiting(row ? invoicingStatusOf(row) : 'missing_pod'), [row])
    const uploads = useDrawerUploads({ loadNumber, loadId: boardLoadId, onFiled: reread, onPodFiled: podFiled })

    // Opening the drawer reads the load's row now, so the state shown is the card's, not the table's last read.
    useEffect(() => {
        let live = true
        void chatRef.current
            .ensureTab()
            .then((tabId) => refreshInvoicingRow(tabId, loadNumber))
            .then(() => {
                if (live) setVersion((value) => value + 1)
            })
            .catch(() => undefined)
            .finally(() => {
                if (live) setRowRead(true)
            })
        return () => {
            live = false
        }
        // The chat bridge is stable for the page; the drawer reads once per load.
    }, [loadNumber])

    // A POD just filed: its check runs in the background, so the row is read every few seconds until the state changes.
    useEffect(() => {
        if (!awaiting) return
        const started = Date.now()
        const timer = setInterval(() => {
            if (Date.now() - started > POD_CHECK_WAIT_MS) {
                setAwaiting(null)
                clearInterval(timer)
                return
            }
            void chatRef.current
                .ensureTab()
                .then((tabId) => refreshInvoicingRow(tabId, loadNumber))
                .then((next) => {
                    setVersion((value) => value + 1)
                    if (next && invoicingStatusOf(next) !== awaiting) {
                        setAwaiting(null)
                        clearInterval(timer)
                    }
                })
                .catch(() => undefined)
        }, POD_CHECK_POLL_MS)
        return () => clearInterval(timer)
    }, [awaiting, loadNumber])

    if (!row && rowRead) {
        // Not an invoicing row (not delivered yet, or not found): the POD's own view still shows the board's files; nothing to invoice here.
        const showDocument = view === 'document' && load
        return (
            <ReviewShell
                onClose={onClose}
                title={showDocument ? 'Proof of delivery' : 'Review invoice draft'}
                subtitle={`Load #${loadNumber}`}
                footer={
                    <Button size="sm" variant="ghost" onClick={onClose}>
                        Close
                    </Button>
                }
            >
                {showDocument ? (
                    <DocumentView loadNumber={loadNumber} load={load} />
                ) : (
                    <p className="text-sm text-foreground-muted">
                        Load #{loadNumber} is not on the invoicing list: it is not delivered yet, or it was not found.
                    </p>
                )}
            </ReviewShell>
        )
    }

    if (!row) {
        return (
            <ReviewShell
                onClose={onClose}
                title="Review invoice draft"
                subtitle={`Load #${loadNumber}`}
                footer={
                    <Button size="sm" variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                }
            >
                <div className="flex items-center gap-2 text-sm text-foreground-muted" role="status">
                    <Spinner size={16} /> Loading the load…
                </div>
            </ReviewShell>
        )
    }

    const status = invoicingStatusOf(row)
    const loadId = row.load_id ?? ''
    const ratePending = ratePendingOf(row)
    const rateTyped = ratePending ? parseRateAmount(details.rateText) : null
    const linehaul = ratePending ? null : (row.amount_due_usd ?? null)
    const terms = details.terms || defaultTermsOf(row)
    const total = invoiceTotalOf(ratePending ? (rateTyped ?? 0) : (linehaul ?? 0), details.charges)
    const approvable = canApprove(row, rateTyped) && !busy
    // Skip & submit creates the draft, which needs a rate confirmation: with none on file it is not offered.
    const skippable = status === 'needs_review' && !rateConMissingOf(row)

    /** The create call from the drawer's values, or the first thing that stops it. */
    const draftParams = (): { params: CreateInvoiceParams } | { message: string } => {
        if (!details.invoiceDate) return { message: 'Invoice date is required.' }
        const charges = chargeErrors(details.charges)
        if (charges) return { message: charges }
        const changed = terms !== defaultTermsOf(row)
        return {
            params: createParamsOf({
                loadNumber,
                invoiceDate: details.invoiceDate === openedOn ? undefined : details.invoiceDate,
                paymentTerms: changed ? (terms as PaymentTerms) : undefined,
                invoiceNumber: details.invoiceNumber,
                charges: details.charges,
            }),
        }
    }

    /** Runs one flow step with the chat's tab, showing its refusal in the drawer. */
    const run = async (step: (tabId: string) => Promise<StepResult>): Promise<StepResult | undefined> => {
        setBusy(true)
        setError(null)
        try {
            const tabId = await chat.ensureTab()
            const result = await step(tabId)
            setVersion((value) => value + 1)
            void Promise.resolve(chat.refreshThread()).catch(() => undefined)
            // An older chat card of this load draws its own copy of the row: read it again too, so it follows the change in place.
            if (result.ok && loadId) void refreshLoadRow(loadId, loadNumber)
            // A draft made here is an invoice a factoring batch takes: the chat's factoring offers count it.
            if (result.ok) void useFactoringBatchableStore.getState().refresh(tabId, true)
            if (!result.ok) setError(result.message)
            return result
        } catch {
            setError('The change did not go through. Try again in a moment.')
            return undefined
        } finally {
            setBusy(false)
        }
    }

    const confirm = () => {
        // v17 says a short reason in a toast; the field keeps what was typed.
        const reasonProblem = confirmReasonError(confirmReason)
        if (reasonProblem) return showSnackbar(SnackbarType.WARNING, reasonProblem)
        if (!loadId) return
        void run((tabId) => confirmDocumentMatch(tabId, loadId, loadNumber, confirmReason)).then((result) => {
            if (result?.ok) setConfirmReason('')
        })
    }

    const approve = () => {
        const built = draftParams()
        if ('message' in built) return setError(built.message)
        if (ratePending && rateTyped === null) return setError('Enter the linehaul rate in dollars.')
        void run((tabId) =>
            approveAndCreate({
                tabId,
                loadId: loadId,
                loadNumber,
                params: built.params,
                rate: ratePending && rateTyped !== null ? rateTyped : undefined,
            })
        ).then((result) => {
            if (result?.ok) {
                showSnackbar(SnackbarType.SUCCESS, DRAFT_CREATED)
                onClose()
            }
        })
    }

    const skipAndSubmit = () => {
        const reasonProblem = skipReasonError(skipReason)
        if (reasonProblem) return showSnackbar(SnackbarType.WARNING, reasonProblem)
        const built = draftParams()
        if ('message' in built) return setError(built.message)
        void run((tabId) => skipAndCreate({ tabId, loadId: loadId, loadNumber, reason: skipReason, params: built.params })).then((result) => {
            if (result?.ok) {
                showSnackbar(SnackbarType.SUCCESS, DRAFT_CREATED)
                onClose()
            }
        })
    }

    const brokerLine = `${row.broker ? `${row.broker} · ` : ''}Load #${loadNumber}`

    if (view !== 'review') {
        const isDocument = view === 'document'
        return (
            <ReviewShell
                onClose={onClose}
                title={isDocument ? 'Proof of delivery' : 'Rate confirmation'}
                subtitle={brokerLine}
                footer={
                    <Button size="sm" variant="secondary" onClick={() => setView('review')}>
                        <span aria-hidden>←</span> Back to invoice review
                    </Button>
                }
            >
                {isDocument ? <DocumentView row={row} loadNumber={loadNumber} load={load} /> : <RateConView row={row} loadNumber={loadNumber} load={load} />}
            </ReviewShell>
        )
    }

    const invoiced = status === 'draft' || status === 'sent' || status === 'batch_prepared'
    if (invoiced) {
        return (
            <ReviewShell
                onClose={onClose}
                title="Invoice draft"
                subtitle={brokerLine}
                footer={
                    <Button size="sm" variant="ghost" onClick={onClose}>
                        Close
                    </Button>
                }
            >
                {row.invoice_id ? (
                    <DraftPreview invoiceId={row.invoice_id} invoiceNumber={row.invoice_number} />
                ) : (
                    <p className="text-sm text-foreground-muted">This load is invoiced. Its invoice is on the Invoices page.</p>
                )}
            </ReviewShell>
        )
    }

    const footer = (
        <>
            <Button size="sm" variant="outline" onClick={onClose}>
                Cancel
            </Button>
            {skippable && (
                <Button size="sm" variant="secondary" disabled={busy} loading={busy && skipping} onClick={skipping ? skipAndSubmit : () => setSkipping(true)}>
                    {skipping ? 'Confirm skip & submit' : 'Skip & submit'}
                </Button>
            )}
            <Button size="sm" disabled={!approvable} loading={busy} onClick={approve}>
                Approve & create draft
            </Button>
        </>
    )

    return (
        <ReviewShell onClose={onClose} title="Review invoice draft" subtitle={brokerLine} footer={footer}>
            <div className="flex flex-col">
                <div className="flex items-center justify-between gap-3">
                    <h3 className="text-base font-semibold text-foreground">Load #{loadNumber}</h3>
                    <InvoicingStatusBadge status={status} label={badgeLabelOf(row, status)} />
                </div>
                <RouteBox load={load} loading={loadLoading} />
                <p className="text-xs text-foreground-muted">
                    Driver: {row.driver_assigned === false ? 'Not assigned' : (row.driver ?? '—')} · Delivery completed
                </p>
                <DocumentsSection
                    row={row}
                    status={status}
                    load={load}
                    loadId={row.load_id ?? ''}
                    loadNumber={loadNumber}
                    awaitingCheck={awaiting !== null}
                    confirmReason={confirmReason}
                    busy={busy}
                    onConfirmReason={setConfirmReason}
                    onConfirm={confirm}
                    onView={(kind) => setView(kind === 'rate' ? 'rate' : 'document')}
                    uploads={uploads}
                />
                <InvoiceDetailsSection
                    values={{ ...details, terms }}
                    onChange={(patch) => setDetails((current) => ({ ...current, ...patch }))}
                    linehaul={linehaul}
                    error={error}
                    total={total}
                    beforeError={skipping && skippable ? <SkipSubmit reason={skipReason} onReason={setSkipReason} /> : undefined}
                />
            </div>
        </ReviewShell>
    )
}

/**
 * The invoice review drawer of the invoicing chat: opened by a row's Review for one load. Confirms the POD match, saves a missing rate,
 * and creates the invoice draft; nothing is sent. The host renders it once; it shows nothing while no review is open.
 */
export const InvoiceReviewDrawer = () => {
    const loadNumber = useInvoiceReview((state) => state.loadNumber)
    const loadId = useInvoiceReview((state) => state.loadId)
    const mode = useInvoiceReview((state) => state.mode)
    const close = useInvoiceReview((state) => state.close)
    const row = useInvoicingRowsStore((state) => (loadNumber ? state.rows[loadNumber] : undefined))
    if (!loadNumber) return null
    return <ReviewBody key={`${loadNumber}:${mode}`} loadNumber={loadNumber} loadId={loadId} mode={mode} row={row} onClose={close} />
}
